import { apiQuery, type LedgerFilters, type LedgerPage } from '../features/locations/ledger';

const API_URL = process.env.REACT_APP_API_URL || '/api';

async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    return body?.error || body?.details || fallback;
  } catch {
    return fallback;
  }
}

/** The file name the server put in Content-Disposition, or a fallback. */
function fileNameOf(response: Response, fallback: string): string {
  const match = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '');
  return match ? match[1] : fallback;
}

export const materialLedgerService = {
  /** One page of the ledger, newest first, for the given filters. */
  list: async (filters: LedgerFilters, page: number, pageSize: number): Promise<LedgerPage> => {
    const query = apiQuery(filters, { page: String(page), pageSize: String(pageSize) });
    const response = await fetch(`${API_URL}/stock-movements?${query}`, { credentials: 'include' });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to load the ledger'));
    return (await response.json()) as LedgerPage;
  },

  /**
   * Downloads the CSV for a date window plus the page's other filters. Fetched rather than
   * linked, so a refusal (too many rows, window too long) comes back as a message to show.
   */
  exportCsv: async (filters: LedgerFilters): Promise<string> => {
    const response = await fetch(`${API_URL}/stock-movements/export?${apiQuery(filters)}`, { credentials: 'include' });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to export the ledger'));
    const fileName = fileNameOf(response, 'material-ledger.csv');
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return fileName;
  },
};
